import Vue from 'vue';
import { kebabCase } from 'lodash-es';
import dayjs from 'dayjs';
import router from './../router';
import isLocalStorageAvailable from '@/helpers/isLocalStorageAvailable';
import isMobile from '@/helpers/isMobileDevice';
import {
    FulfillmentMethodsEnum,
    ThemeVariablesEnum,
    FontKeysEnum,
    AllowedKeysEnum,
    AllergenDisplayEnum,
    ItemModalContentPositionEnum
} from '@/enums';

import {
    initializeVenueAnalytics,
    enableAnalyticsPlugins
} from '@/services/analytics';
import installCurrencyFilter from '@/plugins/currencyFilter';
import {
    applyTheme,
    mountCustomFont,
    setCustomVariable,
    getThemePreview
} from '@/services/theme.service';
import { setNoIndex, setFavicon } from '@/services/seo.service';
import * as venueService from '@/services/venue.service';
import * as tableService from '@/services/table.service';
import { validateStaffToken } from '@/services/staff-token.service';
import prepTimeToLabel from '@/helpers/prepTimeToLabel';

const state = {
    isLoading: 0,
    venue: {
        id: '',
        logo: '',
        name: '',
        address: '',
        phoneNumber: '',
        acceptsDelivery: true,
        acceptsPickup: true,
        acceptsPreOrders: false,
        acceptsInStore: false,
        deliveryFee: 0,
        serviceCharge: 0,
        tipLabel: '',
        primaryColour: '',
        headerImage: '',
        description: '',
        tableDescriptor: '',
        tableScreenImage: '',
        enableOrderNotes: true,
        enableItemNotes: false,
        venueHours: [],
        visualMenu: false,
        activePaymentSystem: '',
        optInMessage: '',
        minInStore: 0,
        menuNotice: '',
        acceptsPat: null,
        metaTitle: '',
        metaDescription: '',
        showCurrencySymbol: true,
        categoryNavImages: 'NONE',
        categoryRouting: 'PAGES',
        modalImageStyle: 'medium',
        comoEnabled: false,
        imageSize: null,
        comoUserIdentifier: null,
        cartImages: 'disabled',
        modalLayoutStyle: null,
        monetaryDisplayStrategy: null,
        allergenDisplay: AllergenDisplayEnum.DEFAULT,
        frontChatId: null,
        superchatApplicationKey: null,
        tawktoPropertyId: null,
        tawktoWidgetId: null,
        formattedAddress: null,
        googleMapsUrl: null,
        itemDescriptionLines: null,
        customSections: [],
        itemContentPosition: ItemModalContentPositionEnum.DEFAULT
    },
    tableAreas: null,
    staffTokenValid: false
};

const mutations = {
    setDeliveryFeeFromZone(state, fee) {
        state.venue.deliveryFee = fee;
    },
    SAVE_VENUE(state, payload) {
        state.venue = payload;
    },
    SAVE_TABLES(state, payload) {
        state.tableAreas = payload;
    },
    FETCH_START(state) {
        state.isLoading++;
    },
    FETCH_END(state) {
        state.isLoading--;
    },
    SET_VISUAL_MENU(state, payload) {
        state.venue.visualMenu = payload;
    },
    clearAutoAppliedDiscountCodes(state) {
        state.venue.autoAppliedDiscountCode = [];
    },
    SET_VENUE_VALUE(state, { key, value }) {
        if (
            typeof state.venue[key] === 'object' &&
            state.venue[key] !== null &&
            !Array.isArray(state.venue[key])
        ) {
            state.venue = {
                ...state.venue,
                [key]: {
                    ...state.venue[key],
                    ...value
                }
            };
        } else {
            state.venue[key] = value;
        }
    },
    SET_STAFF_TOKEN_VALID(state, isValid) {
        state.staffTokenValid = isValid;
    }
};

const getters = {
    isLoading(state) {
        return state.isLoading;
    },
    pickupSlots(state) {
        return state.venue.pickupSlots;
    },
    findTableById: state => id => {
        if (!state.tableAreas) {
            return null;
        }

        return state.tableAreas
            .reduce((arr, item) => arr.concat(item.tables), [])
            .find(sub => sub.id === id);
    },
    autoAppliedDiscountCode({ venue }, getters, { cart }) {
        return (
            ((venue.autoAppliedDiscountCode || false)[0] || false)
                .fulfillmentMethods || []
        ).includes(cart.fulfillmentMethod)
            ? venue.autoAppliedDiscountCode[0]
            : null;
    },
    venueCoordinates(state) {
        // @TODO Venue address coordinates are stored incorrectly :(
        return [
            state.venue.address.lat || null,
            state.venue.address.lng || null
        ];
    },
    venueValue: state => value => {
        return state.venue[value];
    },
    selectedTable(state) {
        if (!isLocalStorageAvailable()) {
            return null;
        }

        const key = `selected-table-venue-${state.venue.id}`;
        const item = localStorage.getItem(key);

        if (!item) {
            return null;
        }

        const { tableId, expiry } = JSON.parse(item);

        if (new Date().getTime() > expiry) {
            localStorage.removeItem(key);

            return null;
        }

        return tableId;
    },
    selectedArea(state, getters, rootState) {
        const area = state.tableAreas?.find(area =>
            area.tables.find(sub => sub.id === rootState.cart.selectedTableId)
        );

        return area;
    },
    isPatEnabled(state, getters) {
        const selectedArea = getters.selectedArea;

        if (
            state.venue.acceptsPat &&
            selectedArea &&
            selectedArea.acceptsPat !== undefined
        ) {
            return selectedArea.acceptsPat;
        }

        return state.venue.acceptsPat;
    },
    tipVariant(state) {
        return state.venue.acceptTips;
    },
    tipPatVariant(state) {
        return state.venue.acceptPatTips;
    },
    mandatoryTipSettings(state) {
        return state.venue.mandatoryTipSettings;
    },
    prepTimeRaw(state, getters, rootState) {
        let prepTimeByMethod;

        if (
            [
                FulfillmentMethodsEnum.IN_STORE,
                FulfillmentMethodsEnum.DELIVERY
            ].includes(rootState.cart.fulfillmentMethod)
        ) {
            prepTimeByMethod = Math.abs(
                state.venue.prepTime + rootState.cart.deliveryTime
            );
        } else {
            prepTimeByMethod = Math.abs(state.venue.collectionPrepTime);
        }

        return prepTimeByMethod;
    },
    prepTime(state, getters) {
        return prepTimeToLabel(getters.prepTimeRaw);
    },
    smartReviewsEnabled(state) {
        return !!state.venue.reviewLinks.length;
    },
    userInformationSettings(state, getters, rootState) {
        const checkoutSettings = state.venue.checkoutSettings?.find(
            ({ fulfillmentMethod }) =>
                fulfillmentMethod === rootState.cart.fulfillmentMethod
        );

        if (checkoutSettings) {
            return {
                email: checkoutSettings.email,
                phone: checkoutSettings.phone,
                firstName: checkoutSettings.firstName,
                lastName: checkoutSettings.lastName,
                companyName: checkoutSettings.companyName
            };
        }

        return {
            email: 'required',
            phone: 'enabled',
            firstName: true,
            lastName: true,
            companyName: 'disabled'
        };
    },
    cutlerySetting(state, getters, rootState) {
        const checkoutSettings = state.venue.checkoutSettings?.find(
            ({ fulfillmentMethod }) =>
                fulfillmentMethod === rootState.cart.fulfillmentMethod
        );

        return checkoutSettings ? checkoutSettings.cutlery : false;
    },
    products(state, getters, rootState, rootGetters) {
        return rootGetters['menu/products'];
    },
    categories(state, getters, rootState, rootGetters) {
        return rootGetters['menu/categories'];
    },
    isClosed(state) {
        if (
            state.venue.acceptsPreOrders &&
            state.venue.alwaysOpenForPreorders
        ) {
            return false;
        }

        if (!state.venue.venueHours) {
            return true;
        }

        const currentTime = dayjs();
        const dayOfWeek = (currentTime.day() || 7) - 1;
        const today = state.venue.venueHours.filter(
            interval => interval.day === dayOfWeek
        );

        for (const interval of today) {
            let openTime = dayjs(interval.openTime, 'HH:mm:ss');
            let closeTime = dayjs(interval.closeTime, 'HH:mm:ss');

            if (
                dayjs(currentTime, 'HH:mm:ss').isAfter(openTime) &&
                dayjs(currentTime, 'HH:mm:ss').isBefore(closeTime)
            ) {
                return false;
            }
        }

        return true;
    }
};

const actions = {
    async validateStaffToken({ commit }, token) {
        try {
            const { data } = await validateStaffToken(token);
            commit('SET_STAFF_TOKEN_VALID', data.valid === true);
            return data.valid === true;
        } catch (error) {
            commit('SET_STAFF_TOKEN_VALID', false);
            return false;
        }
    },
    async getVenue({ dispatch, commit, rootGetters }, venueSlug) {
        if (venueSlug === '404') {
            await router.replace({ name: '404' });
            return false;
        }

        commit('FETCH_START');

        const isStoreDesigner = rootGetters['helpers/isStoreDesigner'];

        try {
            const {
                data: { venue }
            } = await venueService.get(venueSlug);

            commit('SAVE_VENUE', venue);

            // Update Growthbook attributes with venue context
            if (Vue.prototype.$growthbook) {
                await Vue.prototype.$growthbook.updateAttributes({
                    venueId: venue.id,
                    accountId: venue.accountId,
                    venueSlug: venue.slug
                });
            }

            dispatch('marketing/captureMarketingDetails', true, { root: true });

            commit('deliveryAddress/setCountry', venue.address.country, {
                root: true
            });

            if (venue.acceptsInStore) {
                dispatch('getTables', venue.id);
                commit(
                    'cart/setFulfillmentMethod',
                    FulfillmentMethodsEnum.IN_STORE,
                    { root: true }
                );
            }

            if (venue.menu[0] && venue.menu[0].id) {
                dispatch('getMenuCategories', { id: venue.menu[0].id });
            }

            if (venue.branding || venue.fonts) {
                await applyTheme(venue.branding, venue.fonts);
            }

            if (venue?.noIndex || venue?.acceptsPat) {
                setNoIndex();
            }

            if (venue?.faviconImage) {
                setFavicon(venue.faviconImage);
            }

            installCurrencyFilter(venue, isStoreDesigner);

            // Initialize analytics
            if (!isStoreDesigner) {
                const analyticsInstance = initializeVenueAnalytics(
                    venue,
                    isStoreDesigner
                );
                await dispatch('setupOpenReplay');

                const sushidogIds = [8709, 7187, 7571, 7182, 7572, 9017, 9315];
                if (
                    !isMobile() &&
                    sushidogIds.includes(venue.id) &&
                    analyticsInstance
                ) {
                    console.log('Showing Intercom');
                    analyticsInstance.plugins.intercom.show();
                }
            }

            // router.afterEach(() => {
            //     if (Vue.prototype.$analytics) {
            //         Vue.prototype.$analytics.page();
            //     }
            // });

            // Apply theme if theme parameter is present
            const themeParam = router?.currentRoute?.query?.theme;
            if (themeParam) {
                try {
                    const { data } = await getThemePreview(themeParam);
                    for (const [key, value] of Object.entries(data.theme)) {
                        await dispatch('setVenueValue', { key, value });
                    }
                } catch (error) {
                    console.error('Failed to load theme:', error);
                }
            }

            commit('FETCH_END');
        } catch (e) {
            if (e.response) {
                // client received an error response (5xx, 4xx)
                if (e.response.status === 404) {
                    router.replace({ name: '404' });
                }
                throw new Error(`API Response ${e}`);
            } else if (e.request) {
                // client never received a response, or request never left
                throw new Error(`API Request ${e}`);
            } else {
                // anything else
                throw new Error(`Unknown API ${e}`);
            }
        }
    },
    async checkMenuVersion() {
        // @TODO decide on menu versions
        return true;
        // const menu = state.venue.menu;
        //
        // if (menu.length && menu[0].deliverectId) {
        //     const { data: version } = await this.$axiosRetry.get(
        //         `/menus/${menu[0].id}/version`
        //     );
        //
        //     if (version !== menu[0].version) {
        //         dispatch('cart/emptyCart', {}, { root: true });
        //         dispatch('getMenuCategories', state.venue.menu[0].id);
        //         state.venue.menu[0].version = version;
        //
        //         return false;
        //     }
        // }
        //
        // return true;
    },
    async getMenuCategories({ dispatch }, { id, lang = null }) {
        await dispatch('menu/getMenuCategories', { id, lang }, { root: true });
    },
    async getTables({ dispatch, commit }, venueId) {
        commit('FETCH_START');

        try {
            const { data } = await tableService.getTables(venueId);

            commit('SAVE_TABLES', data);
            setTimeout(() => {
                dispatch('checkIfAreaAndTableIsAvailable', venueId);
            }, 500);
        } catch (e) {
            throw new Error(`API ${e}`);
        } finally {
            commit('FETCH_END');
        }
    },
    async checkIfAreaAndTableIsAvailable({ dispatch, state, commit }, venueId) {
        if (!isLocalStorageAvailable()) {
            return;
        }

        const key = `selected-table-venue-${venueId}`;
        const item = localStorage.getItem(key);

        if (item) {
            const { tableId } = JSON.parse(item);
            let selectedTable;

            const selectedArea = state.tableAreas.find(area => {
                selectedTable = area.tables.find(table => table.id === tableId);

                return selectedTable;
            });

            if (!selectedTable) {
                localStorage.removeItem(key);
                await commit('cart/setTable', null, {
                    root: true
                });
            }

            if (
                (selectedArea && !selectedArea.active) ||
                (selectedTable && !selectedTable.active)
            ) {
                await dispatch('triggerTableClosedModal');
            }
        }
        await dispatch('triggerTableSelectionModal');
    },
    triggerTableSelectionModal({ state, rootState }) {
        if (
            [
                'orderStatus',
                'Privacy',
                'checkout',
                'orderReview',
                'LandingPage',
                'billStatus',
                'scan'
            ].includes(router.currentRoute.name)
        ) {
            return false;
        }

        if (
            !state.venue.visualMenu &&
            !rootState.cart.selectedTableId &&
            state.venue.acceptsInStore &&
            !state.venue.requireTableId
        ) {
            this._vm.$modal.show('table-selection-modal');
        }
    },
    triggerFulfillmentMethodModal({ state, commit, rootGetters }) {
        if (state.venue.visualMenu) {
            return false;
        }

        if (
            state.venue &&
            state.venue.acceptsDelivery === true &&
            state.venue.acceptsPickup === true
        ) {
            if (rootGetters['cart/fulfillmentMethod'] == null) {
                this._vm.$modal.show('fulfillment-method-modal');
            }
        } else if (
            state.venue.acceptsDelivery &&
            state.venue.deliveryZones &&
            state.venue.deliveryZones.length > 0
        ) {
            commit(
                'cart/setFulfillmentMethod',
                FulfillmentMethodsEnum.DELIVERY,
                { root: true }
            );

            this._vm.$modal.show('fulfillment-method-modal');
        } else if (state.venue.acceptsDelivery === true) {
            commit(
                'cart/setFulfillmentMethod',
                FulfillmentMethodsEnum.DELIVERY,
                { root: true }
            );
        } else {
            commit('cart/setFulfillmentMethod', FulfillmentMethodsEnum.PICKUP, {
                root: true
            });
        }
    },
    triggerCateringFulfillmentMethodModal({ state, commit, rootGetters }) {
        if (state.venue.visualMenu) {
            return false;
        }

        if (
            state.venue &&
            state.venue.acceptsDelivery === true &&
            state.venue.acceptsPickup === true
        ) {
            if (rootGetters['cart/fulfillmentMethod'] == null) {
                this._vm.$modal.show('catering-fulfillment-method-modal');
            }
        } else if (
            state.venue.acceptsDelivery &&
            state.venue.deliveryZones &&
            state.venue.deliveryZones.length > 0
        ) {
            commit(
                'cart/setFulfillmentMethod',
                FulfillmentMethodsEnum.CATERING_DELIVERY,
                { root: true }
            );

            this._vm.$modal.show('catering-fulfillment-method-modal');
        } else if (state.venue.acceptsDelivery === true) {
            commit(
                'cart/setFulfillmentMethod',
                FulfillmentMethodsEnum.CATERING_DELIVERY,
                { root: true }
            );
        } else {
            commit(
                'cart/setFulfillmentMethod',
                FulfillmentMethodsEnum.CATERING_PICKUP,
                {
                    root: true
                }
            );
        }
    },
    triggerTableClosedModal() {
        this._vm.$modal.show('custom-message-modal', {
            text: 'tableClosed'
        });
    },
    clearAutoAppliedDiscountCodes({ commit }) {
        commit('clearAutoAppliedDiscountCodes');
    },
    enablePlugins({ state }) {
        const pluginData = {
            googleTagManagerId: state.venue?.googleTagManagerId,
            klaviyoApiKey: state.venue?.klaviyoApiKey,
            facebookPixelId: state.venue?.facebookPixelId
        };
        enableAnalyticsPlugins(pluginData);
    },
    async setVenueValue({ commit, rootGetters, getters }, { key, value }) {
        const fontKeys = Object.values(FontKeysEnum);

        const allowedKeys = [
            ...Object.values(AllowedKeysEnum),
            ...Object.values(FontKeysEnum),
            ...Object.values(ThemeVariablesEnum)
        ];

        if (!allowedKeys.includes(key)) {
            return;
        }

        if (key === AllowedKeysEnum.BUTTON_FONT) {
            const isStoreDesigner = rootGetters['helpers/isStoreDesigner'];

            const val = value ? getters.venueValue('fonts')[value].name : null;
            setCustomVariable(key, val, isStoreDesigner);

            value = { buttonFont: value };
            key = 'branding';
        }

        if (fontKeys.includes(key)) {
            const cssVariable = `--${kebabCase(key)}`;
            const isValue = !!value?.variants?.length;
            const isButtonFont =
                getters.venueValue('branding').buttonFont ===
                key.replace('Font', '');

            if (isValue) {
                await mountCustomFont(value.name, value.variants, cssVariable);

                if (isButtonFont) {
                    document.documentElement.style.setProperty(
                        '--button-font',
                        value.name
                    );
                }
            } else {
                document.documentElement.style.removeProperty(cssVariable);

                if (isButtonFont) {
                    document.documentElement.style.removeProperty(
                        '--button-font'
                    );

                    commit('SET_VENUE_VALUE', {
                        key: 'branding',
                        value: { buttonFont: null }
                    });
                }
            }

            const fontKey = key.replace('Font', '');
            value = { [fontKey]: value };
            key = 'fonts';
        }

        if (key === 'borderRadiusButton' && value) {
            const borderRadiusOptions = {
                none: '0px',
                sm: '2px',
                lg: '8px',
                full: '9999px'
            };

            value = borderRadiusOptions[value];
        }

        if (Object.values(ThemeVariablesEnum).includes(key)) {
            const isStoreDesigner = rootGetters['helpers/isStoreDesigner'];

            setCustomVariable(key, value, isStoreDesigner);

            value = { [key]: value };
            key = 'branding';
        }

        if (key === AllowedKeysEnum.SHOW_CURRENCY_SYMBOL) {
            const isStoreDesigner = rootGetters['helpers/isStoreDesigner'];
            const venue = { ...state.venue, showCurrencySymbol: value };
            installCurrencyFilter(venue, isStoreDesigner);
        }

        if (key === AllowedKeysEnum.MAP_STYLE_URL) {
            key = 'mapStyle';
            value = { mapStyleUrl: value };
        }

        if (key === AllowedKeysEnum.MAP_MARKER_STORE_IMG) {
            key = 'mapStyle';
            value = { mapMarkerStoreImg: value };
        }

        commit('SET_VENUE_VALUE', { key, value });
    },
    async setupOpenReplay({ state }) {
        // Check if OpenReplay is already initialized
        if (window.openReplayTrackerInitialized) {
            console.log('OpenReplay tracker is already initialized');
            return;
        }

        // Sushidog
        if (state.venue.accountId === '487aa959-b7e6-430d-a1a5-edc9a09ab249') {
            try {
                // Dynamically import OpenReplay
                const { default: Tracker } =
                    await import('@openreplay/tracker');

                // Initialize the tracker
                const tracker = new Tracker({
                    projectKey: '1BWsxwFDyso39FRoEipy'
                });
                await tracker.start({
                    obscureText: true,
                    network: {
                        capturePayload: true
                    }
                });

                // Mark as initialized
                window.openReplayTrackerInitialized = true;
                console.log('OpenReplay tracker initialized');
            } catch (error) {
                console.error(
                    'Failed to initialize OpenReplay tracker:',
                    error
                );
            }
        }
    }
};

export default {
    namespaced: true,
    getters,
    mutations,
    state,
    actions
};
